// src/components/resources/tools/generators/EmailThread/types.ts
//
// Shared type definitions and defaults for the Email Thread Generator.
// Extracted from the former single-file component so the generator's
// subcomponents and the pure helpers can share one source of truth.

/**
 * Supported email client types for thread preview.
 */
export type EmailClient = 'gmail' | 'outlook'

/**
 * Represents a single email message in a thread.
 */
export interface EmailMessage {
  id: string
  sender: string
  email: string
  timestamp: string
  content: string
  isCurrentUser?: boolean
  starred?: boolean
}

/**
 * Configuration for the email thread preview.
 */
export interface EmailThreadConfig {
  client: EmailClient
  subject: string
  messages: EmailMessage[]
  sidebarCollapsed?: boolean
  starred?: boolean
  archived?: boolean
  showAvatars?: boolean
  highlightReply?: boolean
}

/**
 * `onConfigChange` accepts either a partial patch OR a functional updater. The
 * updater form lets in-preview toolbar toggles (star / archive / sidebar) flip
 * from the CURRENT committed state rather than the `deferredConfig` prop the
 * preview renders from — otherwise, on a slow device, two quick taps before the
 * deferred snapshot catches up both compute the same `!deferred.x` and the
 * second tap is a no-op (the control appears stuck).
 */
export type EmailConfigChange = (
  updates: Partial<EmailThreadConfig> | ((prev: EmailThreadConfig) => Partial<EmailThreadConfig>),
) => void

/**
 * A single Gmail-style message row. Extracted and wrapped in `React.memo`
 * so that editing one message (or any thread-level field) re-renders only
 * the row whose `message` object actually changed — not every styled
 * bubble in the thread. `updateMessage` rebuilds `messages` with
 * `m.id === id ? { ...m } : m`, so unchanged messages keep their object
 * reference and this memo bails out of re-rendering them. `onToggleStar`
 * must be a stable (parent-hoisted) callback or the memo is defeated.
 */
export interface GmailMessageRowProps {
  message: EmailMessage
  isLast: boolean
  showAvatars: boolean
  highlightReply: boolean
  onToggleStar: (messageId: string) => void
}

/**
 * A single Outlook-style message row — `React.memo`'d for the same
 * per-row re-render isolation as `GmailMessageRow`. Outlook rows are
 * purely presentational (no per-message controls), so they take no
 * callbacks.
 */
export interface OutlookMessageRowProps {
  message: EmailMessage
  isLast: boolean
  showAvatars: boolean
  highlightReply: boolean
}

/**
 * Default messages for the thread preview.
 */
export const DEFAULT_MESSAGES: EmailMessage[] = [
  {
    id: '1',
    sender: 'Jordan Keller',
    email: 'jordan@adsights.ai',
    timestamp: '9:42 AM',
    content:
      'Following up on the Q2 creative pipeline. Sharing the latest AI-assisted mocks and the approved paid social variants. Let me know if we should stage these for the April sprint.',
  },
  {
    id: '2',
    sender: 'Maya Chen',
    email: 'maya@adsights.ai',
    timestamp: '10:17 AM',
    content:
      'The mocks look great. Can we add a LinkedIn carousel variant? Would love to see a testimonial block and a data viz tile for the case study slide.',
  },
]

/**
 * Default configuration for the email thread preview.
 */
export const DEFAULT_CONFIG: EmailThreadConfig = {
  client: 'gmail',
  subject: 'Q2 Creative Sprint — Launch Assets Review',
  messages: DEFAULT_MESSAGES,
  sidebarCollapsed: false,
  starred: false,
  archived: false,
  showAvatars: true,
  highlightReply: true,
}
